/**
 * Экономика варианта (макет v2-05, нижний блок + подвал эталона Ольховатки).
 *
 * Считается сервисом, а не хранится (ADR-066 D4), поэтому цифры приезжают
 * вместе с картой. С этапа 2 ADR-071 здесь: статьи затрат в трёх измерениях
 * (всего / на га / на тонну — как подвал эталона), блок продукции (валовка в
 * физическом весе, товарная валовка) и деньги на всю площадь назначения.
 *
 * Выручки и маржи здесь нет намеренно: цена реализации живёт на уровне
 * варианта плана (ADR-071 D7) — карта отвечает за затраты и себестоимость.
 */
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TileGrid } from '@/components/ui/tile-grid';
import {
  ARTICLE_ORDER,
  ARTICLE_TITLES,
  blockTitle,
  formatMoney,
  formatNumber,
  formatPerHa,
  type CardCost,
} from '@/lib/planning/techCards';
import { formatPricePerUnit } from '@/lib/planning/money';

export function CostSummary({ cost }: { cost: CardCost | null }) {
  if (!cost) {
    return (
      <p className="text-sm text-muted-foreground">
        Себестоимость пока не посчитана — в варианте нет операций.
      </p>
    );
  }

  const blocks = Object.entries(cost.by_block)
    .filter(([, value]) => value > 0)
    .sort((a, b) => b[1] - a[1]);
  const sum = blocks.reduce((acc, [, value]) => acc + value, 0);
  const assigned = cost.basis_area === 'assigned';
  const articles = ARTICLE_ORDER.map((article) => ({
    article,
    money: cost.by_article?.[article],
  })).filter((row) => (row.money?.total ?? 0) !== 0);

  return (
    <div className="space-y-4">
      <TileGrid cols={4}>
        <Tile
          label="Прямые затраты на га"
          value={formatPerHa(cost.per_ha)}
          hint={
            (cost.wip_per_ha ?? 0) > 0
              ? `в т.ч. НЗП ${formatPerHa(cost.wip_per_ha ?? 0)} — осенние работы прошлого сезона под этот урожай`
              : undefined
          }
        />
        <Tile
          label="Себестоимость тонны"
          value={formatPricePerUnit(cost.cost_per_ton, 'т')}
          hint={
            cost.yield_c_ha === null
              ? 'нужна плановая урожайность варианта'
              : cost.marketable_cost_per_ton != null
                ? `физвес при ${formatNumber(cost.yield_c_ha)} ц/га · в зачётном весе ${formatPricePerUnit(cost.marketable_cost_per_ton, 'т')}`
                : `физический вес, при ${formatNumber(cost.yield_c_ha)} ц/га`
          }
        />
        <Tile
          label="Затраты на всю площадь"
          value={formatMoney(cost.total)}
          hint={
            cost.area_ha === null
              ? 'площадь берётся из назначения варианту плана'
              : `${formatNumber(cost.area_ha)} га`
          }
        />
        <Tile
          label="Валовый сбор"
          value={cost.gross_t == null ? '—' : `${formatNumber(cost.gross_t)} т`}
          hint={
            cost.gross_t == null
              ? 'нужны площадь назначения и плановая урожайность'
              : cost.marketable_gross_t != null
                ? `физвес · товарная ${formatNumber(cost.marketable_gross_t)} т`
                : 'в физическом весе'
          }
        />
      </TileGrid>

      {articles.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap items-baseline gap-2">
              <div className="text-sm font-semibold text-foreground">Статьи затрат</div>
              <span className="text-xs text-muted-foreground">
                {assigned
                  ? 'всего · на гектар · на тонну — как в сводной техкарте'
                  : 'карта не назначена варианту плана — показываем расчёт на 1 га'}
              </span>
            </div>
            <Table className="mt-2">
              <TableHeader>
                <TableRow className="text-xs">
                  <TableHead>Статья</TableHead>
                  <TableHead className="text-right">Всего</TableHead>
                  <TableHead className="text-right">На гектар</TableHead>
                  <TableHead className="text-right">На тонну</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {articles.map(({ article, money }) => (
                  <TableRow key={article}>
                    <TableCell className="py-2 text-muted-foreground">
                      {ARTICLE_TITLES[article]}
                    </TableCell>
                    <TableCell className="py-2 text-right font-medium whitespace-nowrap">
                      {assigned ? formatMoney(money?.total ?? null) : '—'}
                    </TableCell>
                    <TableCell className="py-2 text-right whitespace-nowrap">
                      {formatPerHa(money?.per_ha ?? (assigned ? null : (money?.total ?? null)))}
                    </TableCell>
                    <TableCell className="py-2 text-right whitespace-nowrap">
                      {formatPricePerUnit(money?.per_ton, 'т')}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow className="font-semibold">
                  <TableCell className="py-2">Прямые затраты</TableCell>
                  <TableCell className="py-2 text-right whitespace-nowrap">
                    {assigned ? formatMoney(cost.total) : '—'}
                  </TableCell>
                  <TableCell className="py-2 text-right whitespace-nowrap">
                    {formatPerHa(cost.per_ha)}
                  </TableCell>
                  <TableCell className="py-2 text-right whitespace-nowrap">
                    {formatPricePerUnit(cost.cost_per_ton, 'т')}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {blocks.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="text-sm font-semibold text-foreground">Сколько стоит каждый блок</div>
            <ul className="mt-2 space-y-1 text-sm">
              {blocks.map(([kind, value]) => (
                <li key={kind} className="flex min-h-10 items-center gap-4 py-2 leading-6">
                  <span className="text-muted-foreground">{blockTitle(kind)}</span>
                  <span className="ml-auto font-medium text-foreground">
                    {formatPerHa(value)}
                  </span>
                  <span className="w-16 text-right text-xs text-muted-foreground">
                    {Math.round((value / sum) * 100)}%
                  </span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {cost.gaps.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="text-sm font-semibold text-foreground">
              Посчитано не всё — чего не хватает
            </div>
            <ul className="mt-2 list-disc space-y-1 pl-6 text-sm text-muted-foreground">
              {cost.gaps.slice(0, 8).map((gap) => (
                <li key={gap}>{gap}</li>
              ))}
            </ul>
            {cost.gaps.length > 8 && (
              <p className="mt-2 text-xs text-muted-foreground">
                и ещё {cost.gaps.length - 8} — заполните бюджетные справочники сезона.
              </p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Tile({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="mt-1 text-lg font-semibold text-foreground">{value}</div>
        {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
      </CardContent>
    </Card>
  );
}
